interface FilterAdjustments {
  // Basic CSS Filters
  contrast?: number // 0-200 (100 is neutral)
  brightness?: number // 0-200 (100 is neutral)
  saturation?: number // 0-200 (100 is neutral)
  sepia?: number // 0-100 (0 is neutral)
  blur?: number // 0-10 (0 is neutral)
  grayscale?: number // 0-100 (0 is neutral)
  hueRotate?: number // 0-360 (0 is neutral)
  invert?: number // 0-100 (0 is neutral)
  exposure?: number // -100 to 100 (0 is neutral)

  /**
   * Backlight recovery, 0-100 (0 is neutral / off). Lifts a subject that the camera
   * underexposed because it metered for a bright background. Unlike every other control
   * here it is *local*, so it can brighten a shadowed face without touching a blown sky.
   */
  backlightRecovery?: number

  // Advanced Effects
  shadows?: number // -100 to 100 (0 is neutral)
  highlights?: number // -100 to 100 (0 is neutral)
  temperature?: number // -100 to 100 (0 is neutral)
  vignette?: number // 0-100 (0 is neutral)
  grain?: number // 0-100 (0 is neutral)

  whites?: number // -100 to 100 (0 is neutral)
  blacks?: number // -100 to 100 (0 is neutral)
  texture?: number // -50 to 50 (0 is neutral) - reduced range for less dramatic effect

  // Dreamy — see applyDreamyEffects
  bloom?: number // 0-100 (0 is neutral)
  softFocus?: number // 0-100 (0 is neutral)
  halation?: number // 0-100 (0 is neutral)

  // New Basic Adjustments
  tint?: number // -100 to 100 (0 is neutral)
  clarity?: number // -100 to 100 (0 is neutral)
  dehaze?: number // -100 to 100 (0 is neutral)
  vibrance?: number // -100 to 100 (0 is neutral)
  luminance?: number // -100 to 100 (0 is neutral)

  // Sharpening
  sharpeningAmount?: number // 0-150 (0 is neutral)
  sharpeningRadius?: number // 0.5-3.0 (1.0 is neutral)
  sharpeningDetail?: number // 0-100 (25 is neutral)
  sharpeningMasking?: number // 0-100 (0 is neutral)

  // Noise Reduction
  noiseReductionLuminance?: number // 0-100 (0 is neutral)
  noiseReductionDetail?: number // 0-100 (50 is neutral)
  noiseReductionContrast?: number // 0-100 (0 is neutral)
  noiseReductionColor?: number // 0-100 (0 is neutral)
  noiseReductionSmoothness?: number // 0-100 (50 is neutral)

  // SVG Filters
  svgBlendMode?: 'screen' | 'overlay' | 'darken'
  svgBlendColor?: string
}

type CanvasContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

// Helper to check if we are in a browser or worker environment
const isBrowserOrWorker =
  (typeof window !== 'undefined' && typeof window.document !== 'undefined') ||
  (typeof self !== 'undefined' && typeof self.postMessage === 'function')

// Helper to check if we are in the main browser thread
const isMainBrowserThread = typeof window !== 'undefined' && typeof window.document !== 'undefined'

/* ------------------------------------------------------------------------- *
 * ctx.filter fallback for WebKit
 *
 * Safari does not implement `CanvasRenderingContext2D.filter`. MDN's compat data lists
 * it as Safari 18+ *behind the off-by-default "Canvas Filters" preference*, so on a
 * stock iPad the assignment silently becomes an inert JS property (it even reads back
 * the string you set) and the drawImage that follows copies the frame through untouched.
 *
 * That dropped the entire CSS half of every manual filter on web booths — contrast,
 * brightness, saturation, sepia, blur, grayscale, hue-rotate, invert — while the
 * pixel-loop half (shadows, temperature, vignette, grain) still ran. The result looks
 * filtered but wrong: the Cikstik karcis iPad printed Jelaga (`saturation: 0`) in full
 * colour while the same project looked correct on the operator's desktop. 68% of the
 * filters in the fleet lean on at least one of these eight operations.
 *
 * Below is the same eight operations done arithmetically against the pixels we already
 * hold — each one the matrix the Filter Effects spec defines, applied a stage at a time
 * so intermediate clipping matches the native path.
 * ------------------------------------------------------------------------- */

/** Row-major 3x4 colour matrix: 3 coefficients + 1 offset (in 0-255 space) per channel. */
type ColorMatrix = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
]

let canvasFilterSupport: boolean | null = null

/**
 * Probe `ctx.filter` by behaviour rather than by user-agent: the WebKit preference can be
 * switched on, and a future Safari will ship it unflagged. `saturate(0%)` turns pure red
 * into a neutral grey wherever the property is honoured.
 */
function supportsCanvasFilter(): boolean {
  if (canvasFilterSupport !== null) return canvasFilterSupport
  try {
    let canvas: HTMLCanvasElement | OffscreenCanvas | null = null
    if (isMainBrowserThread) {
      canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
    } else if (typeof OffscreenCanvas !== 'undefined') {
      canvas = new OffscreenCanvas(1, 1)
    }
    if (!canvas) {
      canvasFilterSupport = false
      return canvasFilterSupport
    }
    const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasContext | null
    if (!ctx) {
      canvasFilterSupport = false
      return canvasFilterSupport
    }
    ctx.fillStyle = 'rgb(255, 0, 0)'
    ctx.fillRect(0, 0, 1, 1)
    ctx.filter = 'saturate(0%)'
    ctx.drawImage(ctx.canvas, 0, 0)
    ctx.filter = 'none'
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
    canvasFilterSupport = Math.abs(r - g) < 8 && Math.abs(g - b) < 8
  } catch {
    canvasFilterSupport = false
  }
  return canvasFilterSupport
}

const brightnessMatrix = (k: number): ColorMatrix => [k, 0, 0, 0, 0, k, 0, 0, 0, 0, k, 0]

const contrastMatrix = (k: number): ColorMatrix => {
  const intercept = (0.5 - 0.5 * k) * 255
  return [k, 0, 0, intercept, 0, k, 0, intercept, 0, 0, k, intercept]
}

const saturateMatrix = (s: number): ColorMatrix => [
  0.213 + 0.787 * s,
  0.715 - 0.715 * s,
  0.072 - 0.072 * s,
  0,
  0.213 - 0.213 * s,
  0.715 + 0.285 * s,
  0.072 - 0.072 * s,
  0,
  0.213 - 0.213 * s,
  0.715 - 0.715 * s,
  0.072 + 0.928 * s,
  0
]

const sepiaMatrix = (amount: number): ColorMatrix => {
  const v = 1 - amount
  return [
    0.393 + 0.607 * v,
    0.769 - 0.769 * v,
    0.189 - 0.189 * v,
    0,
    0.349 - 0.349 * v,
    0.686 + 0.314 * v,
    0.168 - 0.168 * v,
    0,
    0.272 - 0.272 * v,
    0.534 - 0.534 * v,
    0.131 + 0.869 * v,
    0
  ]
}

const hueRotateMatrix = (degrees: number): ColorMatrix => {
  const radians = (degrees * Math.PI) / 180
  const c = Math.cos(radians)
  const s = Math.sin(radians)
  return [
    0.213 + c * 0.787 - s * 0.213,
    0.715 - c * 0.715 - s * 0.715,
    0.072 - c * 0.072 + s * 0.928,
    0,
    0.213 - c * 0.213 + s * 0.143,
    0.715 + c * 0.285 + s * 0.14,
    0.072 - c * 0.072 - s * 0.283,
    0,
    0.213 - c * 0.213 - s * 0.787,
    0.715 - c * 0.715 + s * 0.715,
    0.072 + c * 0.928 + s * 0.072,
    0
  ]
}

const invertMatrix = (amount: number): ColorMatrix => {
  const slope = 1 - 2 * amount
  const offset = amount * 255
  return [slope, 0, 0, offset, 0, slope, 0, offset, 0, 0, slope, offset]
}

/**
 * One matrix, one tight pass. Stages are deliberately kept as separate passes rather than
 * collapsed into a single matrix: each has to clamp before the next reads it, exactly as
 * the native path does when a stage lands in its intermediate surface. Jelaga is a live
 * example — its `brightness(120%)` clips highlights to white *before* `saturate(0%)`
 * decides their grey. Writing through the Uint8ClampedArray gives that clamping, and its
 * round-half-to-even, for free.
 *
 * Chaining the stages inside one pass looks like the cheaper shape and is not: measured
 * on WebKit over a 1280x720 frame with Jelaga's three stages, interleaved reps, it costs
 * 45ms against 16ms for separate passes (byte-identical output). A fixed matrix in a
 * monomorphic loop optimises far better than a per-pixel inner loop over a matrix list.
 */
function applyColorMatrix(data: Uint8ClampedArray, m: ColorMatrix): void {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    data[i] = m[0] * r + m[1] * g + m[2] * b + m[3]
    data[i + 1] = m[4] * r + m[5] * g + m[6] * b + m[7]
    data[i + 2] = m[8] * r + m[9] * g + m[10] * b + m[11]
  }
}

/** Applies a queued run of colour stages in order, then empties the queue. */
function flushColorMatrices(data: Uint8ClampedArray, pending: ColorMatrix[]): void {
  for (const m of pending) applyColorMatrix(data, m)
  pending.length = 0
}

/**
 * Box-blur widths whose three-pass composition approximates a Gaussian of the given sigma
 * — the approximation the Filter Effects spec itself prescribes for `blur()`.
 */
function boxSizesForGaussian(sigma: number, passes: number): number[] {
  const idealWidth = Math.sqrt((12 * sigma * sigma) / passes + 1)
  let lower = Math.floor(idealWidth)
  if (lower % 2 === 0) lower -= 1
  const upper = lower + 2
  const idealCrossover =
    (12 * sigma * sigma - passes * lower * lower - 4 * passes * lower - 3 * passes) /
    (-4 * lower - 4)
  const crossover = Math.round(idealCrossover)
  const sizes: number[] = []
  for (let i = 0; i < passes; i++) sizes.push(i < crossover ? lower : upper)
  return sizes
}

/** One separable box-blur pass (horizontal then vertical) over a single extracted channel. */
function boxBlurPass(
  channel: Float32Array,
  scratch: Float32Array,
  width: number,
  height: number,
  radius: number
): void {
  const weight = 1 / (radius + radius + 1)
  // The running window for output x is [x - radius, x + radius], edge-clamped. Seed it for
  // x = 0, emit, *then* slide — sliding first would shift the image a pixel per pass.
  for (let y = 0; y < height; y++) {
    const row = y * width
    let accumulator = channel[row] * radius
    for (let x = 0; x <= radius; x++) accumulator += channel[row + Math.min(x, width - 1)]
    for (let x = 0; x < width; x++) {
      scratch[row + x] = accumulator * weight
      accumulator += channel[row + Math.min(x + radius + 1, width - 1)]
      accumulator -= channel[row + Math.max(x - radius, 0)]
    }
  }
  for (let x = 0; x < width; x++) {
    let accumulator = scratch[x] * radius
    for (let y = 0; y <= radius; y++) accumulator += scratch[Math.min(y, height - 1) * width + x]
    for (let y = 0; y < height; y++) {
      channel[y * width + x] = accumulator * weight
      accumulator += scratch[Math.min(y + radius + 1, height - 1) * width + x]
      accumulator -= scratch[Math.max(y - radius, 0) * width + x]
    }
  }
}

/**
 * Frames reaching this path are opaque (camera captures and composited strips), so the
 * three colour channels are blurred directly without premultiplying by alpha.
 *
 * Edges are clamped rather than faded: the native filter treats everything outside the
 * canvas as transparent black, so it darkens the outermost ~3σ band. Measured against
 * Chromium, the interior matches within 4/255 while that border band diverges. Clamping
 * is the better look for a booth photo (no halo), so the difference is deliberate — it
 * affects only the 11 fleet filters that set a non-zero blur.
 */
function blurImageData(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  sigma: number
): void {
  if (sigma <= 0 || width === 0 || height === 0) return
  const pixels = width * height
  const channel = new Float32Array(pixels)
  const scratch = new Float32Array(pixels)
  const sizes = boxSizesForGaussian(sigma, 3)

  for (let offset = 0; offset < 3; offset++) {
    for (let i = 0; i < pixels; i++) channel[i] = data[i * 4 + offset]
    for (const size of sizes) {
      const radius = (size - 1) / 2
      if (radius >= 1) boxBlurPass(channel, scratch, width, height, radius)
    }
    for (let i = 0; i < pixels; i++) data[i * 4 + offset] = channel[i]
  }
}

/**
 * Backlight recovery.
 *
 * Every other tone control in this file is a global curve: it maps an input level to an
 * output level the same way everywhere in the frame. That is exactly why none of them can
 * fix a backlit subject. Measured on a synthetic backlit frame (blown outdoor background,
 * dark-skinned face, hair and shirt in shot) rendered to a 384px receipt and dithered to
 * 1 bit, the face's tonal spread — the thing that decides whether it prints as a face or
 * as a solid black mass — barely moves no matter how the globals are set:
 *
 *     raw capture                          face SD 6.2, 81% ink
 *     Jelaga as shipped                    face SD 6.9, 79% ink
 *     Jelaga retuned as hard as it goes    face SD 6.3, 67% ink
 *     with backlight recovery              face SD 40.9, 59% ink
 *
 * The reason is that the frame as a whole already spans 16-255; it is only the *face* that
 * is squeezed into 28-79. A global curve cannot pull those apart, because every level it
 * would need to lift in the face is also present in the background.
 *
 * So: estimate the lighting as a heavy blur of the frame ("base"), lift that with a gamma
 * curve — which raises shadowed regions and leaves already-bright ones alone, since
 * 1^gamma is still 1 — and scale each pixel by how much its own lighting was lifted. That
 * treats subject and background differently, which is the whole trick.
 *
 * The lift is applied as a *ratio* rather than by adding detail back onto the lifted base.
 * The additive form was tried first and inverts: where the subject is large next to a very
 * bright surround, the blur drags the base above the subject's own level, the detail term
 * goes strongly negative and the face comes out darker than it started (measured x0.84 on
 * a subject filling half a bright frame). The ratio form cannot do that, and the guard
 * below makes it explicit.
 *
 * The blur radius scales with the frame, so a preview frame and a full-resolution capture
 * get the same look rather than the same pixel radius. min-edge/20 was picked by sweep:
 * tighter and the base starts tracking the face itself (which cancels the lift), wider and
 * the tonal separation the print depends on falls away again.
 */
/** Longest edge of the downsampled grid the lighting estimate is built on. */
const BACKLIGHT_BASE_EDGE = 256

function applyBacklightRecovery(imageData: ImageData, strength: number): void {
  const amount = Math.max(0, Math.min(100, strength)) / 100
  if (amount <= 0) return

  const { data, width, height } = imageData
  const pixels = width * height
  if (pixels === 0) return

  // strength 100 lands on gamma 0.5 / gain 1.9, the strongest setting that still looked
  // like a photograph rather than a rubbing in the print tests.
  const gamma = 1 - 0.5 * amount
  const gain = 1 + 0.9 * amount
  const sigma = Math.max(4, Math.min(width, height) / 20)

  // The base is a very heavy blur, so it carries no high-frequency information worth
  // computing at full resolution. Building it on a downsampled grid and sampling back
  // bilinearly is visually identical and turns the blur into a rounding error on the cost:
  // at 2048x2048 the whole stage went from 604ms to 47ms.
  const step = Math.max(1, Math.floor(Math.max(width, height) / BACKLIGHT_BASE_EDGE))
  const bw = Math.max(1, Math.ceil(width / step))
  const bh = Math.max(1, Math.ceil(height / step))

  const luminance = new Float32Array(pixels)
  const base = new Float32Array(bw * bh)
  const counts = new Float32Array(bw * bh)
  for (let y = 0; y < height; y++) {
    const brow = Math.min(bh - 1, (y / step) | 0) * bw
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const level = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
      luminance[y * width + x] = level
      const b = brow + Math.min(bw - 1, (x / step) | 0)
      base[b] += level
      counts[b] += 1
    }
  }
  for (let b = 0; b < base.length; b++) if (counts[b] > 0) base[b] /= counts[b]

  const scratch = new Float32Array(bw * bh)
  for (const size of boxSizesForGaussian(sigma / step, 3)) {
    const radius = (size - 1) / 2
    if (radius >= 1) boxBlurPass(base, scratch, bw, bh, radius)
  }

  // 921k Math.pow calls per preview frame is most of what this stage used to cost. The
  // curve only depends on the base level, so precompute it and interpolate — the base is
  // smooth, and rounding to the nearest table entry would band visibly across a sky.
  const lift = new Float32Array(257)
  for (let v = 0; v <= 256; v++) lift[v] = 255 * Math.pow(Math.min(255, v) / 255, gamma)

  const invStep = 1 / step
  for (let y = 0; y < height; y++) {
    // Bilinear position in the small grid, sampling at pixel centres.
    const gy = Math.min(bh - 1.0001, Math.max(0, (y + 0.5) * invStep - 0.5))
    const y0 = gy | 0
    const y1 = Math.min(bh - 1, y0 + 1)
    const fy = gy - y0
    for (let x = 0; x < width; x++) {
      const gx = Math.min(bw - 1.0001, Math.max(0, (x + 0.5) * invStep - 0.5))
      const x0 = gx | 0
      const x1 = Math.min(bw - 1, x0 + 1)
      const fx = gx - x0
      const top = base[y0 * bw + x0] + (base[y0 * bw + x1] - base[y0 * bw + x0]) * fx
      const bottom = base[y1 * bw + x0] + (base[y1 * bw + x1] - base[y1 * bw + x0]) * fx
      const localLighting = top + (bottom - top) * fy

      const li = localLighting < 0 ? 0 : localLighting > 255 ? 255 : localLighting
      const li0 = li | 0
      const lifted = lift[li0] + (lift[li0 + 1] - lift[li0]) * (li - li0)

      const p = y * width + x
      const level = luminance[p]
      // How much this pixel's own lighting was lifted, applied to the pixel itself.
      const exposed = level * (lifted / (localLighting > 1 ? localLighting : 1))
      // Then push detail away from the lifted local mean, which is what puts tonal
      // separation back into a face the camera flattened.
      let target = lifted + (exposed - lifted) * gain
      // A lift must never end up darkening what it was lifting.
      if (exposed > level && target < level) target = level
      if (target < 0) target = 0
      else if (target > 255) target = 255

      const i = p * 4
      if (level > 1) {
        // Scale the channels together so hue survives the exposure change.
        const ratio = target / level
        data[i] = data[i] * ratio
        data[i + 1] = data[i + 1] * ratio
        data[i + 2] = data[i + 2] * ratio
      } else {
        // Nothing to scale at black; take the target directly rather than staying pinned.
        data[i] = target
        data[i + 1] = target
        data[i + 2] = target
      }
    }
  }
}

/**
 * Arithmetic stand-in for the `ctx.filter` string built in `applyAdjustments`. The order
 * below mirrors that string exactly — CSS filter functions are order-dependent.
 */
function applyCssFilterEquivalent(imageData: ImageData, adjustments: FilterAdjustments): void {
  const { data, width, height } = imageData
  // Colour stages accumulate so they can run as one pass; blur is spatial, so it splits
  // the chain and forces everything queued before it to be applied first.
  const pending: ColorMatrix[] = []

  if (adjustments.contrast !== undefined && adjustments.contrast !== 100) {
    pending.push(contrastMatrix(adjustments.contrast / 100))
  }
  if (adjustments.brightness !== undefined && adjustments.brightness !== 100) {
    pending.push(brightnessMatrix(adjustments.brightness / 100))
  }
  if (adjustments.saturation !== undefined && adjustments.saturation !== 100) {
    pending.push(saturateMatrix(adjustments.saturation / 100))
  }
  if (adjustments.sepia !== undefined && adjustments.sepia !== 0) {
    pending.push(sepiaMatrix(adjustments.sepia / 100))
  }
  if (adjustments.blur !== undefined && adjustments.blur !== 0) {
    flushColorMatrices(data, pending)
    blurImageData(data, width, height, adjustments.blur)
  }
  if (adjustments.grayscale !== undefined && adjustments.grayscale !== 0) {
    pending.push(saturateMatrix(1 - adjustments.grayscale / 100))
  }
  if (adjustments.hueRotate !== undefined && adjustments.hueRotate !== 0) {
    pending.push(hueRotateMatrix(adjustments.hueRotate))
  }
  if (adjustments.invert !== undefined && adjustments.invert !== 0) {
    pending.push(invertMatrix(adjustments.invert / 100))
  }
  flushColorMatrices(data, pending)
}

/**
 * Bloom, soft focus and halation — the "dreamy" effects a LUT cannot express.
 *
 * A LUT maps one input colour to one output colour wherever it appears, which is exactly
 * why it cannot make a highlight bleed into the pixels around it: bleeding is spatial and
 * a LUT has no notion of space. It is also why no overlay image could stand in for these.
 * The glow has to be built out of *this* photo's highlights, and the photo is not in the
 * overlay file.
 *
 * Three variations on one operation:
 *
 *   bloom      neutral glow      highlights, moderate radius
 *   halation   warm glow         only the brightest highlights, wide radius, red-orange
 *   softFocus  milky diffusion   the whole frame, tight radius
 *
 * All three composite back with *screen*, which is what separates them from a blur:
 * screen only ever adds light, so sharp detail survives underneath while a soft halo
 * appears around it. Blending toward the blurred copy instead would destroy that detail
 * and merely duplicate the existing `blur` adjustment.
 *
 * Radii are a fraction of the frame's short edge, following applyBacklightRecovery: a
 * dashboard preview and a full-resolution capture then get the same *look* rather than
 * the same pixel radius.
 *
 * Kept in step with `lib/filters/dreamyEffects.ts` in the dreambooth repo — that is what
 * the dashboard previews with, and a divergence here means the preview stops predicting
 * what the booth prints.
 */

/** Where each effect starts treating a pixel as a highlight. */
const BLOOM_THRESHOLD = 0.7
const HALATION_THRESHOLD = 0.78

/** Blur radius as a fraction of the frame's short edge. Halation is widest by design. */
const BLOOM_SIGMA_RATIO = 1 / 45
const HALATION_SIGMA_RATIO = 1 / 28
const SOFT_FOCUS_SIGMA_RATIO = 1 / 60

/**
 * How much glow reaches the frame at slider value 100.
 *
 * None is 1: at full screen strength each of the three turns the photo into a white wash,
 * which nobody wants and which would make the top half of every slider useless. Capping
 * puts 100 at "strong but still a photograph" so the whole range is worth travelling.
 */
const BLOOM_MAX = 0.85
const HALATION_MAX = 0.7
const SOFT_FOCUS_MAX = 0.5

/** Halation keeps red, sheds most green and nearly all blue — the film-bleed colour. */
const HALATION_GREEN = 0.35
const HALATION_BLUE = 0.1

/**
 * A copy of the frame with everything below `threshold` luminance rolled off to black.
 *
 * A soft knee rather than a hard cut: a hard cut makes the glow trace a visible outline
 * around whatever cleared the threshold, which reads as an artefact rather than as light.
 * Black is the right floor because the result is screened back, and screening black is a
 * no-op — everything under the knee contributes nothing without needing an alpha channel
 * to say so.
 */
function highlightPass(source: Uint8ClampedArray, threshold: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(source.length)
  const span = 1 - threshold
  for (let i = 0; i < source.length; i += 4) {
    const r = source[i]
    const g = source[i + 1]
    const b = source[i + 2]
    const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
    const knee = luma <= threshold ? 0 : Math.min(1, (luma - threshold) / span)
    out[i] = r * knee
    out[i + 1] = g * knee
    out[i + 2] = b * knee
    out[i + 3] = 255
  }
  return out
}

/** `screen` blend of `glow` onto `data`, scaled by `strength` (0-1). In place on `data`. */
function screenOnto(
  data: Uint8ClampedArray,
  glow: Uint8ClampedArray,
  strength: number
): void {
  if (strength <= 0) return
  for (let i = 0; i < data.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const base = data[i + c]
      const over = glow[i + c] * strength
      data[i + c] = 255 - ((255 - base) * (255 - over)) / 255
    }
  }
}

/**
 * Apply whichever of the three effects are non-zero, in place.
 *
 * Returns before allocating anything when all three are at their neutral 0, which is what
 * keeps every filter that predates them on exactly the path it took before.
 */
function applyDreamyEffects(imageData: ImageData, adjustments: FilterAdjustments): void {
  const bloom = adjustments.bloom ?? 0
  const softFocus = adjustments.softFocus ?? 0
  const halation = adjustments.halation ?? 0
  if (bloom <= 0 && softFocus <= 0 && halation <= 0) return

  const { data, width, height } = imageData
  if (width === 0 || height === 0) return

  const shortEdge = Math.min(width, height)
  // Snapshot before any glow lands, so two effects enabled together do not feed each
  // other — bloom's output must not become halation's input.
  const source = new Uint8ClampedArray(data)

  if (bloom > 0) {
    const glow = highlightPass(source, BLOOM_THRESHOLD)
    blurImageData(glow, width, height, Math.max(1, shortEdge * BLOOM_SIGMA_RATIO))
    screenOnto(data, glow, (bloom / 100) * BLOOM_MAX)
  }

  if (halation > 0) {
    const glow = highlightPass(source, HALATION_THRESHOLD)
    for (let i = 0; i < glow.length; i += 4) {
      glow[i + 1] = glow[i + 1] * HALATION_GREEN
      glow[i + 2] = glow[i + 2] * HALATION_BLUE
    }
    blurImageData(glow, width, height, Math.max(1, shortEdge * HALATION_SIGMA_RATIO))
    screenOnto(data, glow, (halation / 100) * HALATION_MAX)
  }

  if (softFocus > 0) {
    // The whole frame, not just its highlights: diffusion lifts the shadows too, which is
    // where the milky, low-contrast half of the look comes from.
    const glow = new Uint8ClampedArray(source)
    blurImageData(glow, width, height, Math.max(1, shortEdge * SOFT_FOCUS_SIGMA_RATIO))
    screenOnto(data, glow, (softFocus / 100) * SOFT_FOCUS_MAX)
  }
}

/**
 * Blend modes an overlay may use — the intersection of canvas `globalCompositeOperation`,
 * CSS `mix-blend-mode` and Photoshop's own names.
 *
 * Mirrors `lib/filters/blendModes.ts` in the dreambooth repo, which is what the dashboard
 * picker is built from. Anything unrecognised falls back to source-over rather than
 * reaching a canvas, where an unknown operation is ignored by some engines and throws in
 * others — and where "no blend mode was chosen" is the right reading anyway.
 */
const BLEND_MODES = new Set([
  'source-over',
  'screen',
  'lighten',
  'multiply',
  'darken',
  'overlay',
  'soft-light',
  'hard-light',
  'color-dodge',
  'color-burn',
  'difference',
  'exclusion',
  'hue',
  'saturation',
  'color',
  'luminosity'
])

/**
 * The CSS spellings, as literals rather than `string`.
 *
 * React types `mixBlendMode` as a closed union, so a plain `string` return would not
 * assign to it — and widening the style object to shut that up would give up the checking
 * everywhere else in it.
 */
type CssBlendMode =
  | 'screen'
  | 'lighten'
  | 'multiply'
  | 'darken'
  | 'overlay'
  | 'soft-light'
  | 'hard-light'
  | 'color-dodge'
  | 'color-burn'
  | 'difference'
  | 'exclusion'
  | 'hue'
  | 'saturation'
  | 'color'
  | 'luminosity'

export function normalizeBlendMode(value: unknown): GlobalCompositeOperation {
  return (
    typeof value === 'string' && BLEND_MODES.has(value) ? value : 'source-over'
  ) as GlobalCompositeOperation
}

/**
 * The CSS `mix-blend-mode` matching a stored canvas blend mode, for the DOM live preview.
 *
 * `undefined` for the neutral mode rather than the string "normal", so a layer that never
 * chose a blend mode does not get a `mix-blend-mode` declaration at all — setting one
 * creates a stacking context, and doing that to every layer in the live strip would be a
 * behaviour change dressed up as a no-op.
 */
export function blendModeToCss(value: unknown): CssBlendMode | undefined {
  const normalized = normalizeBlendMode(value)
  // Every non-neutral member of BLEND_MODES is spelled identically in CSS; only
  // source-over differs, and that is the branch that returns undefined.
  return normalized === 'source-over' ? undefined : (normalized as CssBlendMode)
}

/**
 * The natural pixel size of anything canvas accepts as a draw source.
 *
 * `CanvasImageSource` is a union whose members spell their dimensions differently —
 * `HTMLImageElement` has `naturalWidth`, `SVGImageElement` has neither, the rest have
 * plain `width`/`height`. Returning zeroes for the shapes that cannot answer lets the
 * caller skip the draw instead of scaling by NaN.
 */
function overlayIntrinsicSize(image: CanvasImageSource): { width: number; height: number } {
  const candidate = image as {
    naturalWidth?: number
    naturalHeight?: number
    width?: number | SVGAnimatedLength
    height?: number | SVGAnimatedLength
  }
  if (
    typeof candidate.naturalWidth === 'number' &&
    typeof candidate.naturalHeight === 'number'
  ) {
    return { width: candidate.naturalWidth, height: candidate.naturalHeight }
  }
  if (typeof candidate.width === 'number' && typeof candidate.height === 'number') {
    return { width: candidate.width, height: candidate.height }
  }
  return { width: 0, height: 0 }
}

class FilterUtils {
  private grainTextures: (HTMLCanvasElement | OffscreenCanvas)[] = []
  private static readonly NUM_GRAIN_TEXTURES = 5
  private isClient: boolean
  private sharedImageData: ImageData | null = null
  private sharedBuffer: Uint8ClampedArray | null = null
  private lastCanvasWidth: number = 0
  private lastCanvasHeight: number = 0

  constructor() {
    this.isClient = isBrowserOrWorker
    if (this.isClient) {
      this.createGrainTextures()
    }
  }

  private ensureSharedBuffer(width: number, height: number): void {
    if (width !== this.lastCanvasWidth || height !== this.lastCanvasHeight || !this.sharedBuffer) {
      this.sharedBuffer = new Uint8ClampedArray(width * height * 4)
      this.lastCanvasWidth = width
      this.lastCanvasHeight = height
    }
  }

  private getImageData(ctx: CanvasContext): ImageData {
    const width = ctx.canvas.width
    const height = ctx.canvas.height

    // Reuse existing ImageData if dimensions match
    if (
      this.sharedImageData &&
      this.sharedImageData.width === width &&
      this.sharedImageData.height === height
    ) {
      const currentData = ctx.getImageData(0, 0, width, height)
      this.sharedImageData.data.set(currentData.data)
      return this.sharedImageData
    }

    // Create new ImageData if dimensions don't match
    this.ensureSharedBuffer(width, height)
    const imageData = ctx.getImageData(0, 0, width, height)
    // Create new ImageData with the correct dimensions first
    this.sharedImageData = new ImageData(width, height)
    // Then copy the data from our shared buffer
    this.sharedImageData.data.set(this.sharedBuffer!)
    // Finally copy the actual image data
    this.sharedImageData.data.set(imageData.data)
    return this.sharedImageData
  }

  applyAdjustments(ctx: CanvasContext, adjustments: FilterAdjustments): void {
    if (!this.isClient) return

    // Reset context
    ctx.filter = 'none'
    ctx.globalCompositeOperation = 'source-over'

    // Get initial image data using shared buffer
    const imageData = this.getImageData(ctx)

    // Exposure correction comes before the creative grade: it is fixing what the camera
    // got wrong, not styling it.
    if (adjustments?.backlightRecovery) {
      applyBacklightRecovery(imageData, adjustments.backlightRecovery)
      // The native ctx.filter path re-reads the canvas, so the corrected pixels have to
      // land there before it runs. The WebKit fallback reads imageData directly and would
      // be fine either way.
      ctx.putImageData(imageData, 0, 0)
    }

    // Apply basic CSS filters first
    const filters: string[] = []

    // Basic CSS Filters with neutral values
    if (adjustments?.contrast !== undefined && adjustments.contrast !== 100) {
      filters.push(`contrast(${adjustments.contrast}%)`)
    }
    if (adjustments?.brightness !== undefined && adjustments.brightness !== 100) {
      filters.push(`brightness(${adjustments.brightness}%)`)
    }
    if (adjustments?.saturation !== undefined && adjustments.saturation !== 100) {
      filters.push(`saturate(${adjustments.saturation}%)`)
    }
    if (adjustments?.sepia !== undefined && adjustments.sepia !== 0) {
      filters.push(`sepia(${adjustments.sepia}%)`)
    }
    if (adjustments?.blur !== undefined && adjustments.blur !== 0) {
      filters.push(`blur(${adjustments.blur}px)`)
    }
    if (adjustments?.grayscale !== undefined && adjustments.grayscale !== 0) {
      filters.push(`grayscale(${adjustments.grayscale}%)`)
    }
    if (adjustments?.hueRotate !== undefined && adjustments.hueRotate !== 0) {
      filters.push(`hue-rotate(${adjustments.hueRotate}deg)`)
    }
    if (adjustments?.invert !== undefined && adjustments.invert !== 0) {
      filters.push(`invert(${adjustments.invert}%)`)
    }

    // Apply CSS filters if any
    if (filters.length > 0) {
      if (supportsCanvasFilter()) {
        ctx.filter = filters.join(' ')
        ctx.drawImage(ctx.canvas, 0, 0)
        ctx.filter = 'none'
        // Update shared buffer after CSS filters
        imageData.data.set(ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height).data)
      } else {
        // WebKit ignores ctx.filter entirely — same operations, done on the pixels.
        applyCssFilterEquivalent(imageData, adjustments)
      }
    }

    // Batch process pixel manipulations
    this.processPixelManipulations(imageData, adjustments)

    // Spatial effects, after the per-pixel ones: a glow has to be built from the tones
    // the rest of the chain has already settled on, not from the raw capture.
    applyDreamyEffects(imageData, adjustments)

    // Apply final result
    ctx.putImageData(imageData, 0, 0)

    // Apply effects that require canvas operations last
    if (adjustments?.vignette !== undefined && adjustments.vignette !== 0) {
      this.applyVignette(ctx, adjustments.vignette)
    }

    if (adjustments?.grain !== undefined && adjustments.grain !== 0) {
      this.applyGrain(ctx, adjustments.grain)
    }
  }

  private processPixelManipulations(imageData: ImageData, adjustments: FilterAdjustments): void {
    const data = imageData.data
    const length = data.length

    // Pre-calculate only the values that are defined
    const shadowsIntensity =
      adjustments.shadows !== undefined ? (adjustments.shadows + 100) / 200 : undefined
    const highlightsIntensity =
      adjustments.highlights !== undefined ? (adjustments.highlights + 100) / 200 : undefined
    const temperatureIntensity =
      adjustments.temperature !== undefined ? (adjustments.temperature + 100) / 200 : undefined
    const exposureIntensity =
      adjustments.exposure !== undefined ? adjustments.exposure / 100 : undefined
    const whitesIntensity = adjustments.whites !== undefined ? adjustments.whites / 200 : undefined // Reduced intensity
    const blacksIntensity = adjustments.blacks !== undefined ? adjustments.blacks / 200 : undefined // Reduced intensity
    const textureIntensity =
      adjustments.texture !== undefined ? Math.abs(adjustments.texture) / 100 : undefined
    const clarityIntensity =
      adjustments.clarity !== undefined ? adjustments.clarity / 100 : undefined
    const dehazeIntensity = adjustments.dehaze !== undefined ? adjustments.dehaze / 100 : undefined
    const vibranceIntensity =
      adjustments.vibrance !== undefined ? adjustments.vibrance / 100 : undefined
    const sharpeningAmount =
      adjustments.sharpeningAmount !== undefined && adjustments.sharpeningAmount > 0
        ? adjustments.sharpeningAmount / 100
        : undefined
    const noiseReductionIntensity =
      adjustments.noiseReductionLuminance !== undefined && adjustments.noiseReductionLuminance > 0
        ? adjustments.noiseReductionLuminance / 100
        : undefined

    // Skip if no adjustments are defined
    if (Object.values(adjustments).every((v) => v === undefined)) {
      return
    }

    // Process all pixel manipulations in a single pass
    for (let i = 0; i < length; i += 4) {
      let r = data[i]
      let g = data[i + 1]
      let b = data[i + 2]
      let luminance

      // Apply exposure first as it affects overall brightness
      if (exposureIntensity !== undefined) {
        const factor = exposureIntensity > 0 ? 1 + exposureIntensity : 1 / (1 - exposureIntensity)

        r = Math.min(255, Math.max(0, r * factor))
        g = Math.min(255, Math.max(0, g * factor))
        b = Math.min(255, Math.max(0, b * factor))
      }

      // Shadows and highlights
      if (shadowsIntensity !== undefined || highlightsIntensity !== undefined) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
        const shadowFactor = Math.max(0, 1 - luminance * 2)
        const highlightFactor = Math.max(0, luminance * 2 - 1)
        const totalAdjust =
          (shadowsIntensity !== undefined ? 1 + (shadowsIntensity - 0.5) * shadowFactor : 1) *
          (highlightsIntensity !== undefined
            ? 1 + (highlightsIntensity - 0.5) * highlightFactor
            : 1)

        if (totalAdjust !== 1) {
          r = Math.min(255, Math.max(0, r * totalAdjust))
          g = Math.min(255, Math.max(0, g * totalAdjust))
          b = Math.min(255, Math.max(0, b * totalAdjust))
        }
      }

      // Temperature
      if (temperatureIntensity !== undefined && temperatureIntensity !== 0.5) {
        if (temperatureIntensity > 0.5) {
          const factor = 1 + (temperatureIntensity - 0.5)
          r = Math.min(255, r * factor)
          b = Math.max(0, b * (2 - factor))
        } else {
          const factor = 1 + (0.5 - temperatureIntensity)
          r = Math.max(0, r * (2 - factor))
          b = Math.min(255, b * factor)
        }
      }

      // Whites and blacks with reduced intensity
      if (whitesIntensity !== undefined || blacksIntensity !== undefined) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255

        if (whitesIntensity !== undefined && luminance > 0.5) {
          const factor = 1 + whitesIntensity * (luminance - 0.5) * 2
          r = Math.min(255, r * factor)
          g = Math.min(255, g * factor)
          b = Math.min(255, b * factor)
        }

        if (blacksIntensity !== undefined && luminance < 0.5) {
          const factor = 1 + blacksIntensity * (0.5 - luminance) * 2
          r = Math.max(0, r * factor)
          g = Math.max(0, g * factor)
          b = Math.max(0, b * factor)
        }
      }

      // Clarity
      if (clarityIntensity !== undefined && clarityIntensity !== 0) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
        const localContrast = Math.abs(luminance - 0.5) * 2
        const clarityAdjust = 1 + clarityIntensity * localContrast
        if (clarityAdjust !== 1) {
          r = Math.min(255, Math.max(0, r * clarityAdjust))
          g = Math.min(255, Math.max(0, g * clarityAdjust))
          b = Math.min(255, Math.max(0, b * clarityAdjust))
        }
      }

      // Dehaze
      if (dehazeIntensity !== undefined && dehazeIntensity !== 0) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
        const dehazeAdjust = 1 + dehazeIntensity * (1 - luminance)
        if (dehazeAdjust !== 1) {
          r = Math.min(255, r * dehazeAdjust)
          g = Math.min(255, g * dehazeAdjust)
          b = Math.min(255, b * dehazeAdjust)
        }
      }

      // Vibrance
      if (vibranceIntensity !== undefined && vibranceIntensity !== 0) {
        const max = Math.max(r, g, b)
        const min = Math.min(r, g, b)
        const saturation = max === 0 ? 0 : (max - min) / max

        if (saturation > 0) {
          luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
          const isNearSkinTone =
            r > g &&
            g > b &&
            r > 60 &&
            r < 255 &&
            g > 40 &&
            g < 230 &&
            b > 20 &&
            b < 180 &&
            luminance > 0.2 &&
            luminance < 0.9

          const adjustmentFactor = isNearSkinTone
            ? Math.abs(vibranceIntensity) * 0.3
            : Math.abs(vibranceIntensity) * (1 - saturation)

          if (adjustmentFactor !== 0) {
            const finalFactor = vibranceIntensity > 0 ? 1 + adjustmentFactor : 1 - adjustmentFactor
            const mid = (max + min) / 2
            r = Math.min(255, Math.max(0, mid + (r - mid) * finalFactor))
            g = Math.min(255, Math.max(0, mid + (g - mid) * finalFactor))
            b = Math.min(255, Math.max(0, mid + (b - mid) * finalFactor))
          }
        }
      }

      // Texture with reduced intensity
      if (textureIntensity !== undefined && textureIntensity !== 0) {
        const x = (i / 4) % imageData.width
        const y = Math.floor(i / 4 / imageData.width)
        const noise =
          (this.generatePerlinNoise(x * 0.05, y * 0.05) + 1) * 0.5 * textureIntensity * 0.5 // Reduced by 50%

        if (adjustments.texture! > 0) {
          r = Math.min(255, Math.max(0, r * (1 + noise * 0.25) + noise * 10)) // Reduced multipliers
          g = Math.min(255, Math.max(0, g * (1 + noise * 0.25) + noise * 10))
          b = Math.min(255, Math.max(0, b * (1 + noise * 0.25) + noise * 10))
        } else {
          r = Math.min(255, Math.max(0, r * (1 - noise * 0.15) + (255 - r) * noise * 0.05))
          g = Math.min(255, Math.max(0, g * (1 - noise * 0.15) + (255 - g) * noise * 0.05))
          b = Math.min(255, Math.max(0, b * (1 - noise * 0.15) + (255 - b) * noise * 0.05))
        }
      }

      // Write back RGB values
      data[i] = r
      data[i + 1] = g
      data[i + 2] = b

      // Sharpening (needs neighbors so uses original data)
      if (sharpeningAmount !== undefined) {
        const x = (i / 4) % imageData.width
        const y = Math.floor(i / 4 / imageData.width)

        if (x > 0 && x < imageData.width - 1 && y > 0 && y < imageData.height - 1) {
          for (let j = 0; j < 3; j++) {
            const center = data[i + j]
            const left = data[i - 4 + j]
            const right = data[i + 4 + j]
            const top = data[i - imageData.width * 4 + j]
            const bottom = data[i + imageData.width * 4 + j]

            const laplacian = center * 4 - left - right - top - bottom
            if (laplacian !== 0) {
              data[i + j] = Math.min(255, Math.max(0, center + laplacian * sharpeningAmount))
            }
          }
        }
      }

      // Noise reduction (needs neighbors so uses original data)
      if (noiseReductionIntensity !== undefined) {
        const x = (i / 4) % imageData.width
        const y = Math.floor(i / 4 / imageData.width)

        if (x > 0 && x < imageData.width - 1 && y > 0 && y < imageData.height - 1) {
          for (let j = 0; j < 3; j++) {
            const center = data[i + j]
            const left = data[i - 4 + j]
            const right = data[i + 4 + j]
            const top = data[i - imageData.width * 4 + j]
            const bottom = data[i + imageData.width * 4 + j]

            const average = (left + right + top + bottom) / 4
            if (average !== center) {
              data[i + j] = center + (average - center) * noiseReductionIntensity
            }
          }
        }
      }
    }
  }

  private createGrainTextures(): void {
    if (!this.isClient) return

    for (let i = 0; i < FilterUtils.NUM_GRAIN_TEXTURES; i++) {
      const grainCanvas = this.createGrainTexture(1920, 1080, 40)
      this.grainTextures.push(grainCanvas)
    }
  }

  private createGrainTexture(
    width: number,
    height: number,
    grainValue: number
  ): HTMLCanvasElement | OffscreenCanvas {
    if (!this.isClient) {
      // This should not happen due to the constructor check, but as a fallback:
      return isMainBrowserThread
        ? document.createElement('canvas')
        : new OffscreenCanvas(width, height)
    }

    const grainCanvas = isMainBrowserThread
      ? document.createElement('canvas')
      : new OffscreenCanvas(width, height)
    grainCanvas.width = width
    grainCanvas.height = height
    const grainCtx = grainCanvas.getContext('2d', { willReadFrequently: true }) as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D

    if (!grainCtx) return grainCanvas

    const imageData = grainCtx.createImageData(width, height)
    const data = imageData.data
    const grainOpacityFactor = 0.6

    for (let i = 0; i < data.length; i += 4) {
      const value = Math.random() * 255
      data[i] = value
      data[i + 1] = value
      data[i + 2] = value
      data[i + 3] = Math.random() * grainValue * grainOpacityFactor
    }

    grainCtx.putImageData(imageData, 0, 0)
    return grainCanvas
  }

  private applyVignette(ctx: CanvasContext, vignetteStrength: number): void {
    if (!this.isClient) return

    const width = ctx.canvas.width
    const height = ctx.canvas.height
    const centerX = width / 2
    const centerY = height / 2
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY)
    const intensity = (vignetteStrength / 100) * 0.8 // Increased base intensity

    // Create radial gradient with multiple stops for more dramatic effect
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      maxDistance * 0.3, // Inner radius at 30% for stronger center
      centerX,
      centerY,
      maxDistance
    )

    gradient.addColorStop(0, 'rgba(0,0,0,0)')
    gradient.addColorStop(0.7, `rgba(0,0,0,${intensity * 0.5})`) // Mid stop for smoother transition
    gradient.addColorStop(1, `rgba(0,0,0,${intensity})`)

    ctx.globalCompositeOperation = 'multiply' // Changed to multiply for stronger effect
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
    ctx.globalCompositeOperation = 'source-over' // Reset composite operation
  }

  /**
   * Composite a filter's overlay image over the canvas.
   *
   * `blendMode` is the point of the feature. Drawn source-over, a light-leak PNG (an
   * orange smear on black) is a black rectangle over the photo; drawn with `screen`, the
   * black drops out and only the light is added. Same file, opposite results.
   *
   * `fit: "cover"` centre-crops so an overlay authored at one aspect ratio does not
   * distort on a booth running another; "stretch" is the opt-out for overlays drawn to an
   * exact slot ratio.
   *
   * The context is restored to how it was found. `applyGrain` below already leaks a
   * composite operation, and a second leak stacked on it would make the order effects run
   * in start to matter in ways nobody can reason about.
   */
  applyOverlayImage(
    ctx: CanvasContext,
    overlay: {
      image: CanvasImageSource
      blendMode?: string
      opacity?: number
      fit?: 'cover' | 'stretch'
    }
  ): void {
    if (!this.isClient) return

    const opacity = (overlay.opacity ?? 100) / 100
    if (opacity <= 0) return

    const canvasWidth = ctx.canvas.width
    const canvasHeight = ctx.canvas.height
    if (canvasWidth === 0 || canvasHeight === 0) return

    const previousComposite = ctx.globalCompositeOperation
    const previousAlpha = ctx.globalAlpha
    ctx.globalCompositeOperation = normalizeBlendMode(overlay.blendMode)
    ctx.globalAlpha = opacity

    if (overlay.fit === 'stretch') {
      ctx.drawImage(overlay.image, 0, 0, canvasWidth, canvasHeight)
    } else {
      const source = overlayIntrinsicSize(overlay.image)
      if (source.width > 0 && source.height > 0) {
        const scale = Math.max(canvasWidth / source.width, canvasHeight / source.height)
        const drawWidth = source.width * scale
        const drawHeight = source.height * scale
        ctx.drawImage(
          overlay.image,
          (canvasWidth - drawWidth) / 2,
          (canvasHeight - drawHeight) / 2,
          drawWidth,
          drawHeight
        )
      }
    }

    ctx.globalCompositeOperation = previousComposite
    ctx.globalAlpha = previousAlpha
  }

  private applyGrain(ctx: CanvasContext, grainStrength?: number): void {
    if (!this.isClient) return

    if (grainStrength) {
      const grainTexture = this.createGrainTexture(
        ctx.canvas.width,
        ctx.canvas.height,
        grainStrength
      )
      ctx.globalCompositeOperation = 'overlay'
      ctx.drawImage(grainTexture, 0, 0, ctx.canvas.width, ctx.canvas.height)
    } else {
      const randomIndex = Math.floor(Math.random() * this.grainTextures.length)
      const selectedGrainTexture = this.grainTextures[randomIndex]
      ctx.globalCompositeOperation = 'overlay'
      ctx.drawImage(selectedGrainTexture, 0, 0, ctx.canvas.width, ctx.canvas.height)
    }
  }

  // New method to apply SVG filters
  applySVGFilter(ctx: CanvasRenderingContext2D, svgFilter: string): void {
    // This method uses DOM elements like `new Image()` and is not worker-safe.
    // It will only run on the main browser thread.
    if (!isMainBrowserThread) {
      console.warn('SVG filters are not supported in worker threads.')
      return
    }
    // Get the current canvas data
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height)

    // Create a temporary canvas
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = ctx.canvas.width
    tempCanvas.height = ctx.canvas.height
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true })
    if (!tempCtx) return

    // Put the image data on the temp canvas
    tempCtx.putImageData(imageData, 0, 0)

    // Create image with filter
    const img = new Image()
    const svgImage = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${ctx.canvas.width}" height="${ctx.canvas.height}">
        <defs>
          ${svgFilter}
        </defs>
        <image
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          filter="url(#filter)"
          href="${tempCanvas.toDataURL()}"
        />
      </svg>
    `

    // Create SVG blob and apply it
    const svgBlob = new Blob([svgImage], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(svgBlob)

    img.onload = (): void => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  // SVG Filter Generators
  static generateSVGFilter(
    type: 'hueRotate' | 'saturate' | 'blend',
    params: {
      value?: number
      mode?: 'screen' | 'overlay' | 'darken'
      color?: string
    } = {}
  ): string {
    switch (type) {
      case 'hueRotate':
        return `
          <filter id="filter">
            <feColorMatrix type="hueRotate" values="${params.value || 0}"/>
          </filter>
        `
      case 'saturate':
        return `
          <filter id="filter">
            <feColorMatrix type="saturate" values="${(params.value || 100) / 100}"/>
          </filter>
        `
      case 'blend':
        return `
          <filter id="filter">
            <feFlood flood-color="${params.color || '#000000'}" result="flood"/>
            <feBlend mode="${params.mode || 'screen'}" in="SourceGraphic" in2="flood"/>
          </filter>
        `
      default:
        return ''
    }
  }

  // Example SVG filter presets
  static SVGFilters = {
    duotone: `
      <filter id="filter">
        <feColorMatrix type="matrix" values="
          0.33 0.33 0.33 0 0
          0.33 0.33 0.33 0 0
          0.33 0.33 0.33 0 0
          0 0 0 1 0
        "/>
      </filter>
    `,
    grayscale: `
      <filter id="filter">
        <feColorMatrix type="matrix" values="
          0.2126 0.7152 0.0722 0 0
          0.2126 0.7152 0.0722 0 0
          0.2126 0.7152 0.0722 0 0
          0 0 0 1 0
        "/>
      </filter>
    `,
    sepia: `
      <filter id="filter">
        <feColorMatrix type="matrix" values="
          0.393 0.769 0.189 0 0
          0.349 0.686 0.168 0 0
          0.272 0.534 0.131 0 0
          0 0 0 1 0
        "/>
      </filter>
    `
    // Add more static presets as needed
  }

  private generatePerlinNoise(x: number, y: number): number {
    // Implementation of improved Perlin noise
    const permutation = new Array(512)
    const p = [
      151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140, 36, 103, 30, 69,
      142, 8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234, 75, 0, 26, 197, 62, 94, 252, 219,
      203, 117, 35, 11, 32, 57, 177, 33, 88, 237, 149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175,
      74, 165, 71, 134, 139, 48, 27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230,
      220, 105, 92, 41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216, 80, 73, 209,
      76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86, 164, 100, 109, 198,
      173, 186, 3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38, 147, 118, 126, 255, 82, 85, 212,
      207, 206, 59, 227, 47, 16, 58, 17, 182, 189, 28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44,
      154, 163, 70, 221, 153, 101, 155, 167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79,
      113, 224, 232, 178, 185, 112, 104, 218, 246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12,
      191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49, 192, 214, 31, 181, 199, 106, 157,
      184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254, 138, 236, 205, 93, 222, 114, 67, 29,
      24, 72, 243, 141, 128, 195, 78, 66, 215, 61, 156, 180
    ]

    // Extend the permutation table by copying it
    for (let i = 0; i < 256; i++) {
      permutation[i] = p[i]
      permutation[256 + i] = p[i]
    }

    // Get unit cube that contains point
    const X = Math.floor(x) & 255
    const Y = Math.floor(y) & 255

    // Get relative x,y of point in cube
    x -= Math.floor(x)
    y -= Math.floor(y)

    // Compute fade curves for each of x,y
    const u = this.fade(x)
    const v = this.fade(y)

    // Hash coordinates of the 4 cube corners
    const A = permutation[X] + Y
    const AA = permutation[A]
    const AB = permutation[A + 1]
    const B = permutation[X + 1] + Y
    const BA = permutation[B]
    const BB = permutation[B + 1]

    // Add blended results from 4 corners of cube
    return this.lerp(
      v,
      this.lerp(u, this.grad(permutation[AA], x, y), this.grad(permutation[BA], x - 1, y)),
      this.lerp(u, this.grad(permutation[AB], x, y - 1), this.grad(permutation[BB], x - 1, y - 1))
    )
  }

  // Helper functions for Perlin noise
  private fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10)
  }

  private lerp(t: number, a: number, b: number): number {
    return a + t * (b - a)
  }

  private grad(hash: number, x: number, y: number): number {
    const h = hash & 15
    const grad = 1 + (h & 7) // Gradient value 1-8
    const isNeg = h & 8 // Randomly invert gradient
    return (isNeg ? -grad : grad) * x + (isNeg ? -grad : grad) * y
  }
}

const filterUtils = new FilterUtils()
export { filterUtils, type FilterAdjustments, FilterUtils, supportsCanvasFilter }
